import type { Field, FieldType, FieldValidation } from './types';

export const descriptionEnabled = (field: Field) => field.descriptionEnabled ?? (!!field.help || ['rating', 'date', 'time'].includes(field.type));
export const validationKinds = (type: FieldType): FieldValidation['kind'][] => type === 'short_text' ? ['number', 'text', 'length', 'regex'] : type === 'paragraph' ? ['length', 'regex'] : type === 'checkboxes' ? ['selection'] : [];
export const validationEnabled = (field: Field) => (field.validationEnabled ?? !!field.validation) && validationKinds(field.type).length > 0;
export const VALIDATION_OPS: Record<FieldValidation['kind'], { op: string; label: string; args: number }[]> = {
  number: [
    { op: 'gt', label: 'Greater than', args: 1 }, { op: 'gte', label: 'Greater than or equal to', args: 1 },
    { op: 'lt', label: 'Less than', args: 1 }, { op: 'lte', label: 'Less than or equal to', args: 1 },
    { op: 'eq', label: 'Equal to', args: 1 }, { op: 'neq', label: 'Not equal to', args: 1 },
    { op: 'between', label: 'Between', args: 2 }, { op: 'not_between', label: 'Not between', args: 2 },
    { op: 'is_number', label: 'Is number', args: 0 }, { op: 'integer', label: 'Whole number', args: 0 },
  ],
  text: [{ op: 'contains', label: 'Contains', args: 1 }, { op: 'not_contains', label: "Doesn't contain", args: 1 }, { op: 'email', label: 'Email', args: 0 }, { op: 'url', label: 'URL', args: 0 }],
  length: [{ op: 'max', label: 'Maximum character count', args: 1 }, { op: 'min', label: 'Minimum character count', args: 1 }],
  regex: [{ op: 'contains', label: 'Contains', args: 1 }, { op: 'not_contains', label: "Doesn't contain", args: 1 }, { op: 'matches', label: 'Matches', args: 1 }, { op: 'not_matches', label: "Doesn't match", args: 1 }],
  selection: [{ op: 'min', label: 'Select at least', args: 1 }, { op: 'max', label: 'Select at most', args: 1 }, { op: 'exact', label: 'Select exactly', args: 1 }],
};
export function defaultValidation(type: FieldType): FieldValidation {
  const kind = validationKinds(type)[0] ?? 'number';
  return { kind, op: VALIDATION_OPS[kind][0].op, value: '' };
}
export function validationShape(value: unknown): value is FieldValidation {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const v = value as FieldValidation;
  return Object.hasOwn(VALIDATION_OPS, v.kind) && VALIDATION_OPS[v.kind].some(op => op.op === v.op) &&
    ['string', 'number'].includes(typeof v.value) && String(v.value).length <= 10000 &&
    (v.value2 === undefined || (['string', 'number'].includes(typeof v.value2) && String(v.value2).length <= 10000)) &&
    (v.message === undefined || (typeof v.message === 'string' && v.message.length <= 10000));
}
export function validationConfigError(field: Field): string | null {
  if (!validationEnabled(field)) return null;
  const v = field.validation;
  if (!validationShape(v)) return 'Choose a valid response validation rule';
  if (!validationKinds(field.type).includes(v.kind)) return 'Choose a validation type supported by this question';
  const operation = VALIDATION_OPS[v.kind].find(op => op.op === v.op)!;
  if (operation.args === 0) return null;
  if (v.kind === 'regex') {
    if (!String(v.value)) return 'Enter a regular expression pattern';
    try { new RegExp(String(v.value)); } catch { return 'Enter a valid regular expression pattern'; }
  } else if (v.kind === 'text') { if (!String(v.value)) return 'Enter text for the validation rule'; }
  else {
    const a = Number(v.value), b = Number(v.value2);
    if (!String(v.value).trim() || !Number.isFinite(a)) return 'Enter a number for the validation rule';
    if ((v.kind === 'selection' || v.kind === 'length') && (!Number.isInteger(a) || a < 0)) return 'Enter a whole number of zero or greater';
    if (operation.args === 2 && (v.value2 === undefined || !String(v.value2).trim() || !Number.isFinite(b) || b < a)) return 'Enter an upper limit on or above the lower limit';
    if (v.kind === 'selection' && v.op !== 'max' && a > selectableCount(field)) return 'The selection count exceeds the available options';
  }
  return null;
}

/** How many choices a checkbox question offers, counting Other. */
export const selectableCount = (field: Field) => new Set(field.options ?? []).size + (field.allowOther ? 1 : 0);

/** After options are removed, “Select at least 3” with only 2 left becomes “Select at least 2”, and the same for “Select exactly”. */
export function fitSelectionRule(field: Field): Field {
  const rule = field.validation;
  if (field.type !== 'checkboxes' || rule?.kind !== 'selection' || rule.op === 'max' || !(Number(rule.value) > selectableCount(field))) return field;
  return { ...field, validation: { ...rule, value: String(selectableCount(field)) } };
}

/** Whether the editor keeps a rule's value or upper limit when the organizer leaves it. Empty is fine: the rule stays off
 * until it's filled in. Anything else must be a value the rule can use, or the previous value comes back. */
export function acceptableValidationValue(field: Field, rule: FieldValidation, which: 'value' | 'value2'): boolean {
  const text = String(rule[which] ?? '').trim();
  if (!text || rule.kind === 'text') return true;
  if (rule.kind === 'regex') { try { new RegExp(String(rule[which])); return true; } catch { return false; } }
  const n = Number(text);
  if (!Number.isFinite(n)) return false;
  if ((rule.kind === 'length' || rule.kind === 'selection') && (!Number.isInteger(n) || n < 0)) return false;
  if (rule.kind === 'selection' && rule.op !== 'max' && n > selectableCount(field)) return false;
  const other = String(rule[which === 'value' ? 'value2' : 'value'] ?? '').trim();
  if (VALIDATION_OPS[rule.kind].find(op => op.op === rule.op)?.args === 2 && other && Number.isFinite(Number(other))) return which === 'value' ? n <= Number(other) : n >= Number(other);
  return true;
}

/** The most characters a text question accepts: its maximum-length rule, when it has one. */
export function questionTextLimit(field: Field): number | undefined {
  if (field.type !== 'short_text' && field.type !== 'paragraph') return undefined;
  const rule = field.validation;
  return validationEnabled(field) && rule?.kind === 'length' && rule.op === 'max' && Number.isSafeInteger(Number(rule.value)) && Number(rule.value) > 0 ? Number(rule.value) : undefined;
}
